import { useCallback, useEffect, useState } from "react";
import { buildAdminApi, ApiError } from "../api/client.js";
import { getTeamsToken, isInTeams } from "../auth/teams.js";
import type { UnacknowledgedRow } from "../api/types.js";
import { formatDateTime } from "../lib/format.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; rows: UnacknowledgedRow[] }
  | { kind: "needs-teams" }
  | { kind: "error" };

// Self-contained mobile Teams tab: lists unacknowledged appointments for staff.
// It authenticates via Teams SSO (getTeamsToken) — no MSAL. When not running
// inside Teams, or when the API rejects the SSO token (401), it shows a
// friendly "open from Teams" message instead of crashing.
export function UnacknowledgedTab() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      if (!(await isInTeams())) {
        setState({ kind: "needs-teams" });
        return;
      }
      const api = buildAdminApi(API_BASE, getTeamsToken);
      const rows = await api.listUnacknowledged();
      setState({ kind: "ready", rows });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ kind: "needs-teams" });
        return;
      }
      setState({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const count = state.kind === "ready" ? state.rows.length : null;

  return (
    <div className="mx-auto max-w-md px-4 py-4">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-base font-semibold text-slate-900">
          Unacknowledged
          {count !== null ? (
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-sm font-medium text-slate-600">
              {count}
            </span>
          ) : null}
        </h1>
        <button
          type="button"
          onClick={() => void load()}
          disabled={state.kind === "loading"}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </header>

      {state.kind === "loading" ? (
        <p className="text-sm text-slate-600">Loading…</p>
      ) : null}

      {state.kind === "needs-teams" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          Open this from the Teams app to sign in.
        </div>
      ) : null}

      {state.kind === "error" ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          Could not load unacknowledged appointments. Try refreshing.
        </div>
      ) : null}

      {state.kind === "ready" && state.rows.length === 0 ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-center text-sm text-emerald-800">
          ✅ All caught up — no unacknowledged appointments.
        </p>
      ) : null}

      {state.kind === "ready" && state.rows.length > 0 ? (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
          {state.rows.map((row) => (
            <li
              key={row.public_code}
              className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-slate-800 hover:bg-slate-50"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-900">
                  {row.patient_first_name}
                </p>
                <p className="text-slate-600">
                  {formatDateTime(row.scheduled_start)}
                </p>
              </div>
              <span className="shrink-0 rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
                {row.public_code}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
